import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export const RetestControlModal = ({ control, open, onClose, onSaved }) => {
  const { user } = useAuth();
  const [result, setResult] = useState("Pass");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (control) {
      setResult("Pass");
      setNotes("");
    }
  }, [control]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/grc/controls/${encodeURIComponent(control.control_id)}/test`, { result, notes });
      toast.success(`Control ${control.question_id} marked as ${result}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record test");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="retest-control-modal">
        <DialogHeader>
          <DialogTitle className="text-base text-[#003366]">
            Retest Control {control?.question_id}
          </DialogTitle>
          <DialogDescription className="text-xs">Record a manual test result for this control.</DialogDescription>
        </DialogHeader>
        {control && (
          <div className="space-y-4 py-1">
            {control.question_text && (
              <p className="text-xs text-slate-500 line-clamp-2">{control.question_text}</p>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Result</Label>
              <RadioGroup value={result} onValueChange={setResult} className="flex items-center gap-5" data-testid="retest-result-radio">
                {["Pass", "Fail", "NA"].map((r) => (
                  <div key={r} className="flex items-center gap-1.5">
                    <RadioGroupItem value={r} id={`retest-${r}`} data-testid={`retest-radio-${r.toLowerCase()}`} />
                    <Label htmlFor={`retest-${r}`} className="text-sm font-normal cursor-pointer">{r}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tester</Label>
              <Input value={user?.username || ""} readOnly disabled className="h-9 text-sm bg-slate-50" data-testid="retest-tester-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Test evidence reference, observations…"
                className="text-sm min-h-[80px]"
                data-testid="retest-notes"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="retest-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#008080] hover:bg-[#1a51db]"
            data-testid="retest-save-button"
          >
            {saving ? "Saving…" : "Save Test Result"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
